import React, { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { api, getApiBase } from "../api";

export default function IptvPlayer({
  channelId,
  token,
  channelName,
  channelGroup,
  onChannelFailed,
}) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !channelId) return undefined;
    let cancelled = false;
    setMeta(null);
    api(`/api/iptv/channels/${channelId}/play`, { token })
      .then((data) => {
        if (!cancelled) setMeta(data);
      })
      .catch(() => {
        /* Reproducir igual vía proxy aunque falle el probe previo */
      });
    return () => {
      cancelled = true;
    };
  }, [token, channelId]);

  useEffect(() => {
    if (!containerRef.current || !token || !channelId) return undefined;

    let cancelled = false;
    const mount = containerRef.current;
    setError("");
    setLoading(true);

    mount.innerHTML = "";
    const videoEl = document.createElement("video-js");
    videoEl.classList.add("vjs-big-play-centered", "vjs-theme-casasm", "vjs-live");
    mount.appendChild(videoEl);

    const isSafari =
      Boolean(videojs.browser?.IS_ANY_SAFARI) || Boolean(videojs.browser?.IS_IOS);

    const player = videojs(videoEl, {
      controls: true,
      fluid: false,
      fill: true,
      autoplay: true,
      muted: false,
      preload: "auto",
      liveui: true,
      controlBar: {
        children: ["playToggle", "liveDisplay", "volumePanel", "fullscreenToggle"],
      },
      html5: {
        vhs: {
          overrideNative: !isSafari,
          allowSeeksWithinUnsafeLiveWindow: true,
          maxPlaylistRetries: 10,
          xhr: {
            beforeRequest(options) {
              options.headers = options.headers || {};
              options.headers.Authorization = `Bearer ${token}`;
              return options;
            },
          },
        },
      },
    });

    playerRef.current = player;

    function fitPlayer() {
      if (player.isDisposed() || !mount) return;
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      if (w > 0 && h > 0) player.dimensions(w, h);
    }

    function disposePlayer() {
      if (!playerRef.current || playerRef.current.isDisposed()) return;
      try {
        playerRef.current.dispose();
      } catch (_) {
        /* ignore */
      }
      playerRef.current = null;
    }

    function safePlay() {
      if (cancelled || player.isDisposed()) return;
      const attempt = player.play();
      if (attempt && typeof attempt.catch === "function") {
        attempt.catch(() => {});
      }
    }

    const streamUrl = `${getApiBase()}/api/iptv/stream/${channelId}?jwt=${encodeURIComponent(token)}`;
    player.src({ src: streamUrl, type: "application/x-mpegURL" });

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => fitPlayer())
        : null;
    ro?.observe(mount);

    player.ready(() => {
      if (cancelled || player.isDisposed()) return;
      player.volume(1);
      player.muted(false);
      fitPlayer();
      safePlay();
      setLoading(false);
    });

    player.on("playing", () => {
      if (!cancelled) setLoading(false);
    });

    player.on("error", () => {
      if (cancelled || player.isDisposed()) return;
      setLoading(false);
      setError("No se pudo reproducir este canal. Prueba otro de la lista.");
      api(`/api/iptv/channels/${channelId}/dead`, { token, method: "POST" }).catch(() => {});
      onChannelFailed?.(channelId);
    });

    return () => {
      cancelled = true;
      ro?.disconnect();
      disposePlayer();
      mount.innerHTML = "";
    };
  }, [token, channelId, onChannelFailed]);

  const title = meta?.name || channelName || "Canal";
  const group = meta?.group || channelGroup;

  return (
    <div className="dtv-player-panel">
      <div className="dtv-player-meta">
        <span className="dtv-live-dot" aria-hidden="true" />
        <div className="dtv-player-titles">
          <h2>{title}</h2>
          {group ? <p>{group}</p> : null}
        </div>
      </div>

      <div className="dtv-player-stage">
        {loading && !error ? (
          <div className="dtv-player-overlay">
            <span className="dtv-player-spinner" aria-hidden="true" />
            <span>Conectando…</span>
          </div>
        ) : null}
        {error ? (
          <div className="dtv-player-overlay dtv-player-overlay-error">
            <p>{error}</p>
          </div>
        ) : null}
        <div
          className={`dtv-inline-player${error ? " is-hidden" : ""}`}
          ref={containerRef}
          aria-hidden={Boolean(error)}
        />
      </div>
    </div>
  );
}
